/**
 * When a save is refused because somebody else saved first.
 *
 * The server keeps a version on every record and refuses a save made against one that
 * is no longer stored (409 `edit_conflict`), sending the record as it now stands. This
 * module turns that refusal into a choice for the reader instead of a lost edit:
 *
 * - **their version is shown beside the form**, field by field where the two differ, so
 *   re-applying an edit is a decision rather than archaeology;
 * - **"Use their version"** replaces the form with what is stored;
 * - **"Keep mine"** arms the next Save against their version. It does not save — the
 *   reader presses Save themselves, having seen what they are replacing. Saving over the
 *   other version automatically would be the lost update with an extra step.
 *
 * A refusal the server could not describe (the narrow race inside one request) carries
 * no record, and the notice says to reload instead.
 */

import type { JSX } from 'react'
import { useState } from 'react'

import { AccountError, EDIT_CONFLICT } from '../account'
import { t } from '../i18n'

/** What every record the workspace edits carries, so a save can say what it started from. */
export interface Versioned {
  row_version: number
  updated_at?: string | null
}

/** A refused save, with the other version already shaped like the form. */
export interface EditConflict<TFields> {
  /** Their version as form fields, or `null` when the server could not send it. */
  theirs: TFields | null
  /** The version to save against if the reader keeps theirs. */
  rowVersion: number | null
  /** When the other save was made, as the server stamped it. */
  when: string | null
}

/** The conflict in a failed save, or `null` when the failure was something else. */
export function conflictFrom<TRecord extends Versioned, TFields>(
  error: unknown,
  toForm: (record: TRecord) => TFields,
): EditConflict<TFields> | null {
  if (!(error instanceof AccountError) || error.kind !== EDIT_CONFLICT) return null
  const current = error.current as TRecord | undefined
  if (current === undefined) return { theirs: null, rowVersion: null, when: null }
  return { theirs: toForm(current), rowVersion: current.row_version, when: current.updated_at ?? null }
}

/** A form's conflict state, and the reader's two ways out of it. */
export interface ConflictState<TFields> {
  conflict: EditConflict<TFields> | null
  /** Record a failed save: true when it was a conflict, so the caller shows nothing else. */
  refused: (error: unknown) => boolean
  clear: () => void
  takeTheirs: () => void
  keepMine: () => void
}

/**
 * The conflict half of an editor, shared by every form that saves a versioned record.
 *
 * Their version replaces the form; keeping mine aims the next Save at theirs. Neither
 * saves — the reader presses Save having seen what is there.
 */
export function useConflict<TRecord extends Versioned, TFields>(
  toForm: (record: TRecord) => TFields,
  setFields: (fields: TFields) => void,
  setRowVersion: (rowVersion: number) => void,
): ConflictState<TFields> {
  const [conflict, setConflict] = useState<EditConflict<TFields> | null>(null)

  function refused(error: unknown): boolean {
    const found = conflictFrom(error, toForm)
    setConflict(found)
    return found !== null
  }

  function takeTheirs() {
    if (conflict?.theirs) setFields(conflict.theirs)
    if (conflict?.rowVersion != null) setRowVersion(conflict.rowVersion)
    setConflict(null)
  }

  function keepMine() {
    if (conflict?.rowVersion != null) setRowVersion(conflict.rowVersion)
    setConflict(null)
  }

  return { conflict, refused, clear: () => setConflict(null), takeTheirs, keepMine }
}

/** One field's value as the reader would read it in the form. */
function shown(value: unknown): string {
  if (Array.isArray(value)) return value.join(', ')
  if (value === null || value === undefined) return ''
  return String(value)
}

/** The fields whose values differ between the two versions, in the form's own order. */
function differing<TFields extends object>(mine: TFields, theirs: TFields): (keyof TFields)[] {
  return (Object.keys(theirs) as (keyof TFields)[]).filter((key) => shown(mine[key]) !== shown(theirs[key]))
}

export function ConflictNotice<TFields extends object>({
  conflict,
  mine,
  labels,
  onUseTheirs,
  onKeepMine,
}: {
  conflict: EditConflict<TFields>
  mine: TFields
  /** The form's own label for each field, so the comparison reads like the form. */
  labels: Partial<Record<keyof TFields, string>>
  onUseTheirs: () => void
  onKeepMine: () => void
}): JSX.Element {
  const { theirs } = conflict
  if (theirs === null) {
    return (
      <div className="notice" role="alert">
        <strong>{t('conflict.heading')}</strong>
        <p>{t('conflict.reload')}</p>
      </div>
    )
  }

  const changed = differing(mine, theirs)
  return (
    <div className="notice" role="alert">
      <strong>{t('conflict.heading')}</strong>
      <p>{t('conflict.when', { when: conflict.when ?? '' })}</p>
      <p className="muted">{t('conflict.theirs')}</p>
      {changed.length === 0 ? (
        <p>{t('conflict.same')}</p>
      ) : (
        <dl>
          {changed.map((key) => (
            <div key={String(key)}>
              <dt>{labels[key] ?? String(key)}</dt>
              <dd>{shown(theirs[key])}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className="form__row">
        <button type="button" onClick={onUseTheirs}>
          {t('conflict.useTheirs')}
        </button>
        <button type="button" className="link" onClick={onKeepMine}>
          {t('conflict.keepMine')}
        </button>
      </div>
      <p className="muted">{t('conflict.keepMineHint')}</p>
    </div>
  )
}
